import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Calendar, Plus, Trash2, Image as ImageIcon, MapPin, Tag, Sparkles, X,
} from "lucide-react";

const CATEGORIES = [
  { id: "promo", label: "Promo · Soldes" },
  { id: "ouverture", label: "Ouverture · Vernissage" },
  { id: "fete", label: "Fête · Animation" },
  { id: "marche", label: "Marché · Foire" },
  { id: "atelier", label: "Atelier · Démonstration" },
  { id: "evenement", label: "Autre événement" },
];

async function compressImage(file, maxSide = 1280, q = 0.85) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = dataUrl;
  });
  const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  c.getContext("2d").drawImage(img, 0, 0, w, h);
  return c.toDataURL("image/jpeg", q);
}

const empty = { title: "", description: "", category: "promo", start_at: "", end_at: "", image_url: "", location: "", post_on_wall: true };

export default function MerchantEventsPage() {
  const [events, setEvents] = useState([]);
  const [draft, setDraft] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/merchant/events/mine").then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleImage = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image trop volumineuse (max 5 Mo)."); return; }
    const data = await compressImage(file, 1280, 0.85);
    setDraft((d) => ({ ...d, image_url: data }));
  };

  const save = async () => {
    if (!draft.title.trim()) return toast.error("Le titre est obligatoire.");
    if (!draft.start_at) return toast.error("La date de début est obligatoire.");
    setSaving(true);
    try {
      await api.post("/merchant/events", draft);
      toast.success("Événement publié ✓");
      setDraft(empty); setShowForm(false); load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    try { await api.delete(`/merchant/events/${id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="merchant-events-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="phenix-pill bg-sun-100 text-sun-700 mb-2 inline-flex">
            <Calendar size={11} strokeWidth={2.5} /> Mes événements
          </div>
          <h1 className="heading-display text-3xl lg:text-4xl">Publiez vos événements</h1>
          <p className="mt-2 text-sm text-plum-700/70 max-w-xl">
            Promos, ouvertures, ateliers — vos événements seront visibles dans l&apos;onglet <strong>Événements</strong> de la page d&apos;accueil et notifiés aux habitants.
          </p>
        </div>
        <Button onClick={() => setShowForm((v) => !v)} className="btn-pill bg-plum-700 hover:bg-plum-800 text-white h-11 px-5" data-testid="merchant-events-new-btn">
          {showForm ? <X size={15} /> : <Plus size={15} />} {showForm ? "Annuler" : "Nouvel événement"}
        </Button>
      </header>

      {showForm && (
        <section className="phenix-card p-5 sm:p-6 bg-cream-50/50 border-sun-200" data-testid="event-form">
          <h2 className="font-display font-bold text-lg text-plum-900 mb-4 flex items-center gap-2">
            <Sparkles size={16} className="text-sun-600" /> Nouveau
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-[11px] font-bold uppercase tracking-wider text-plum-700/70 mb-1 block">Titre *</Label>
              <Input data-testid="event-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Soldes d'été · -30% sur tout" className="h-11 rounded-xl" maxLength={120} />
            </div>
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-plum-700/70 mb-1 block">Catégorie</Label>
              <select data-testid="event-category" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className="w-full h-11 rounded-xl border border-cream-200 px-3 text-sm bg-white">
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-plum-700/70 mb-1 block">Lieu (optionnel)</Label>
              <Input data-testid="event-location" value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} placeholder="Boutique · 12 Place Champollion" className="h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-plum-700/70 mb-1 block">Date de début *</Label>
              <Input data-testid="event-start" type="date" value={draft.start_at} onChange={(e) => setDraft({ ...draft, start_at: e.target.value })} className="h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-plum-700/70 mb-1 block">Date de fin (optionnel)</Label>
              <Input data-testid="event-end" type="date" value={draft.end_at} onChange={(e) => setDraft({ ...draft, end_at: e.target.value })} className="h-11 rounded-xl" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[11px] font-bold uppercase tracking-wider text-plum-700/70 mb-1 block">Description</Label>
              <Textarea data-testid="event-description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} rows={3} className="rounded-xl resize-none" maxLength={500} placeholder="Détails, conditions, ce qui rend l'événement spécial..." />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[11px] font-bold uppercase tracking-wider text-plum-700/70 mb-1 block">Image (optionnel)</Label>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="event-image-input" onChange={(e) => handleImage(e.target.files?.[0])} />
              <div className="flex items-center gap-3">
                {draft.image_url && <img src={draft.image_url} alt="" className="w-16 h-16 rounded-xl object-cover" />}
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-xl h-11 text-[12px]">
                  <ImageIcon size={14} /> {draft.image_url ? "Changer" : "Téléverser"}
                </Button>
                {draft.image_url && (
                  <button onClick={() => setDraft({ ...draft, image_url: "" })} className="text-[12px] text-pink-600 font-semibold">Retirer</button>
                )}
              </div>
            </div>
            <div className="sm:col-span-2 mt-2">
              <label className="flex items-start gap-3 p-3 rounded-2xl border-2 border-cream-200 hover:border-plum-200 transition-colors cursor-pointer bg-white" data-testid="event-post-on-wall-toggle">
                <input
                  type="checkbox"
                  checked={!!draft.post_on_wall}
                  onChange={(e) => setDraft({ ...draft, post_on_wall: e.target.checked })}
                  className="w-5 h-5 mt-0.5 accent-plum-700 shrink-0"
                />
                <div className="min-w-0">
                  <div className="font-display font-bold text-[14px] text-plum-900 leading-tight flex items-center gap-1.5">
                    📣 Annoncer aussi sur le Mur des commerçants
                  </div>
                  <div className="text-[11px] text-plum-700/65 mt-0.5">
                    {draft.post_on_wall
                      ? "Un post sera créé automatiquement avec l'événement attaché + indication du parking le plus proche."
                      : "Activez pour faire connaître l'événement à tous les habitants via le Mur."}
                  </div>
                </div>
              </label>
            </div>
          </div>
          <div className="flex gap-2 mt-5">
            <Button onClick={save} disabled={saving} className="btn-pill bg-plum-700 hover:bg-plum-800 text-white h-11 px-6" data-testid="event-save-btn">
              {saving ? "Publication…" : "Publier l'événement"}
            </Button>
            <Button variant="outline" onClick={() => { setDraft(empty); setShowForm(false); }} className="rounded-xl h-11">Annuler</Button>
          </div>
        </section>
      )}

      <section className="space-y-3" data-testid="merchant-events-list">
        {events.length === 0 && (
          <div className="phenix-card p-10 text-center bg-gradient-to-br from-cream-50 to-sun-50/40">
            <Calendar size={32} className="mx-auto text-plum-700/30 mb-3" />
            <h3 className="font-display font-bold text-plum-900 mb-1">Aucun événement publié</h3>
            <p className="text-[13px] text-plum-700/60">Créez votre premier événement pour faire venir des clients !</p>
          </div>
        )}
        {events.map((e) => (
          <article key={e.id} className="phenix-card p-4 flex gap-3 items-start" data-testid={`event-card-${e.id}`}>
            {e.image_url ? (
              <img src={e.image_url} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-plum-100 to-lavande-100 grid place-items-center shrink-0">
                <Tag size={20} className="text-plum-700/40" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1.5 mb-1">
                <span className="phenix-pill bg-sun-100 text-sun-700 text-[9px]">{(CATEGORIES.find((c) => c.id === e.category) || {}).label || "Événement"}</span>
              </div>
              <h3 className="font-display font-bold text-base text-plum-900 line-clamp-1">{e.title}</h3>
              <div className="text-[11px] text-plum-700/60 flex flex-wrap gap-2 mt-1">
                <span className="inline-flex items-center gap-1"><Calendar size={10} /> {e.start_at}{e.end_at && ` → ${e.end_at}`}</span>
                {e.location && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {e.location}</span>}
              </div>
              {e.description && <p className="text-[12px] text-plum-700/70 mt-1.5 line-clamp-2">{e.description}</p>}
            </div>
            <button onClick={() => remove(e.id)} className="text-pink-500 hover:text-pink-600 p-2 rounded-lg hover:bg-pink-50" title="Supprimer" data-testid={`event-delete-${e.id}`}>
              <Trash2 size={15} />
            </button>
          </article>
        ))}
      </section>
    </div>
  );
}
